// Dracula Edition buy column (snippets/lettoria-edition-offer-box.liquid) — ONE combo, one button.
// 1. Arrival: ships +2 business days; "on your arm by" = +5 business days after that (the later bound, so it holds).
// 2. Add: ONE /cart/add.js with both lines carrying `_ltr_lp` + `Set` (+ pre-order props when a variant is armed), then
//    /cart.js → CartLinesUpdateEvent so Horizon's drawer opens. The Shopify Function gates on the LINE PROPERTY only —
//    no cart attribute is written (it would outlive the lines and land on the order).
// 3. Price assertion: after the add, the two `_ltr_lp` lines must total data-price per pair; if they do not (Function
//    missing or failed) the button never says "Added" and a visible price error replaces the silent overcharge.
//    No redirect, cart not cleared, no permalink (a permalink cannot carry line properties).
import { CartLinesUpdateEvent } from '@shopify/events';
import { preorderPropsFromDataset } from '@theme/lettoria-preorder-property-builder';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function addBusinessDays(from, n) {
  const d = new Date(from.getTime());
  let left = n;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) left -= 1;
  }
  return d;
}
const fmt = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

function paintArrival() {
  const ships = addBusinessDays(new Date(), 2);
  const arrive = addBusinessDays(ships, 5);
  document.querySelectorAll('[data-ltr-ed-ships]').forEach((el) => { el.textContent = fmt(ships); });
  document.querySelectorAll('[data-ltr-ed-arrive]').forEach((el) => { el.textContent = fmt(arrive); });
  // optional deadline tag ("before Halloween"): shown ONLY while the painted arrival date still lands on or before
  // data-ltr-ed-before="YYYY-MM-DD" — the claim switches itself off the day it would stop being true
  document.querySelectorAll('[data-ltr-ed-before]').forEach((el) => {
    const parts = (el.getAttribute('data-ltr-ed-before') || '').split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => !n)) return;
    const deadline = new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59);
    el.hidden = arrive.getTime() > deadline.getTime();
  });
}

async function cartPost(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}

class LettoriaEditionOffer {
  constructor(root) {
    this.root = root;
    this.cta = root.querySelector('[data-ltr-ed-cta]');
    this.err = root.querySelector('[data-ltr-ed-error]');
    this.items = Array.from(root.querySelectorAll('[data-ltr-ed-items] li'));
    this.busy = false;
    this.unavailable = !!(this.cta && this.cta.hasAttribute('data-ltr-ed-unavailable'));
    if (this.cta) this.cta.addEventListener('click', () => this.add());
    this.wireSticky();
    // proxies (close band) call add() directly so the HC pixel counts ONE click per surface, not proxy + atc
    document.querySelectorAll('[data-ltr-ed-cta-proxy]').forEach((p) =>
      p.addEventListener('click', () => { if (!this.unavailable) this.add(); })
    );
  }

  lines() {
    const key = this.root.getAttribute('data-key');
    return this.items
      .map((li) => {
        const id = parseInt(li.getAttribute('data-variant-id'), 10);
        const props = { _ltr_lp: key, Set: this.root.getAttribute('data-set-name') || '' };
        const po = preorderPropsFromDataset(li);
        if (po) Object.assign(props, po);
        return { id, quantity: 1, properties: props };
      })
      .filter((l) => Number.isFinite(l.id) && l.id > 0);
  }

  buttons() { return [this.cta, ...document.querySelectorAll('[data-ltr-ed-sticky-btn], [data-ltr-ed-cta-proxy]')].filter(Boolean); }

  setState(state) {
    this.buttons().forEach((b) => {
      const label = b.querySelector('[data-ltr-ed-label]');
      if (label) {
        if (!b.__ltrLabel) b.__ltrLabel = label.textContent;
        label.textContent = state === 'busy' ? 'Adding…' : state === 'added' ? 'Added ✓' : b.__ltrLabel;
      }
      b.classList.toggle('is-busy', state === 'busy');
      b.classList.toggle('is-added', state === 'added');
      b.disabled = state !== '' || this.unavailable;
      if (state === 'busy') b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
    });
  }

  async add() {
    if (this.busy || this.unavailable) return;
    const lines = this.lines();
    if (!lines.length) return;
    this.busy = true;
    this.setState('busy');
    if (this.err) this.err.hidden = true;
    try {
      await cartPost(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', { items: lines });
      const ajaxCart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { items: ajaxCart.items, source: 'lettoria-edition', sourceId: 'dracula-edition' },
          }),
        })
      );
      if (this.pairPriceHolds(ajaxCart)) {
        this.setState('added');
      } else {
        // the lines are in the cart but the set price is not: say so instead of celebrating
        this.setState('');
        if (this.err) { this.err.textContent = this.root.getAttribute('data-price-error') || 'The set price has not applied to your cart yet.'; this.err.hidden = false; }
      }
      setTimeout(() => { this.busy = false; this.setState(''); }, 1800);
    } catch (e) {
      console.error('edition add failed', e);
      this.busy = false;
      this.setState('');
      if (this.err) { this.err.textContent = this.root.getAttribute('data-error') || 'Please tap once more.'; this.err.hidden = false; }
    }
  }

  // true when every `_ltr_lp` pair in the cart totals data-price (cents); pairs = min(handbag qty, wallet qty)
  pairPriceHolds(cart) {
    const key = this.root.getAttribute('data-key');
    const want = parseInt(this.root.getAttribute('data-price'), 10);
    const ids = this.items.map((li) => parseInt(li.getAttribute('data-variant-id'), 10));
    const mine = (cart.items || []).filter((it) => it.properties && it.properties._ltr_lp === key && ids.includes(it.variant_id));
    if (!Number.isFinite(want) || mine.length < ids.length) return false;
    const pairs = Math.min(...ids.map((id) => mine.filter((it) => it.variant_id === id).reduce((q, it) => q + it.quantity, 0)));
    const paid = mine.reduce((sum, it) => sum + it.final_line_price, 0);
    return pairs > 0 && paid === want * pairs;
  }

  wireSticky() {
    const bar = document.querySelector('[data-ltr-ed-sticky]');
    if (!bar || !this.cta) return;
    const btn = bar.querySelector('[data-ltr-ed-sticky-btn]');
    if (btn) btn.addEventListener('click', () => this.add());
    let ticking = false;
    const check = () => {
      ticking = false;
      const show = this.cta.getBoundingClientRect().bottom < 0 && !this.cta.hasAttribute('data-ltr-ed-unavailable');
      if (bar.hidden === show) bar.hidden = !show;
    };
    const onScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(check); } };
    // Horizon scrolls inside .page-wrapper as well as window: listen on both.
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    check();
  }
}

function boot() {
  paintArrival();
  document.querySelectorAll('[data-ltr-ed]').forEach((root) => { if (!root.__ltrEd) root.__ltrEd = new LettoriaEditionOffer(root); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', boot); // theme editor re-renders the section
